import { useFormContext } from '@calendly/form'
import { I18n } from '@calendly/i18n'
import { Button } from '@calendly/ui/components/button'
import { IconPlus } from '@calendly/ui/components/glyphs/IconPlus'
import { BodyText, TitleText } from '@calendly/ui/components/typography'
import { UiIcon } from '@calendly/ui/components/ui-icon'
import { colorIconStandard } from '@calendly/ui/theme'
import { useCallback, useState } from 'react'

import { useEnforceUpgrade } from 'components/billing/useEnforceUpgrade'
import { VerticalSortableContext } from 'components/core/sortable'
import {
  EXTERNAL,
  generateBlankCustomLink,
  reorderLinks,
} from 'features/event_type_editor'

import { CustomLinkCard } from './custom_link_card'
import {
  SCHEDULE_ANOTHER_MEETING_EDITING_KEY,
  ScheduleAnotherMeetingCard,
} from './schedule_another_meeting_card'
import {
  addLinkButtonContentCss,
  addLinkButtonCss,
  addLinkButtonTextCss,
  linkListCss,
  sectionDividerCss,
  subsectionCss,
  subsectionHeaderCss,
} from './styles'

const snapshotScheduleAnotherValues = (values) => ({
  anotherEventButtonEnabled: values.anotherEventButtonEnabled,
  anotherEventButtonLabel: values.anotherEventButtonLabel,
})

const snapshotCustomLinkValues = (link) => ({
  content: link.content,
  url: link.url,
  enabled: link.enabled,
})

export const InternalSection = () => {
  const {
    values,
    values: { confirmationPageType, customLinks },
    setFieldValue,
    setFieldTouched,
  } = useFormContext()
  const enforceUpgrade = useEnforceUpgrade('custom_links')
  const [editingKey, setEditingKey] = useState(null)
  const [draftSnapshot, setDraftSnapshot] = useState(null)

  const undeletedLinks = customLinks.filter(({ _destroy }) => !_destroy)
  const isEditing = editingKey !== null

  const startEditing = useCallback((key, snapshot) => {
    setDraftSnapshot(snapshot)
    setEditingKey(key)
  }, [])

  const finishEditing = useCallback(() => {
    setDraftSnapshot(null)
    setEditingKey(null)
  }, [])

  const handleReorder = (items) => {
    setFieldValue('customLinks', reorderLinks(customLinks, items))
    setFieldTouched('customLinks', true)
  }

  const handleScheduleAnotherEdit = () => {
    startEditing(SCHEDULE_ANOTHER_MEETING_EDITING_KEY, snapshotScheduleAnotherValues(values))
  }

  const handleScheduleAnotherCancel = () => {
    if (draftSnapshot) {
      setFieldValue('anotherEventButtonEnabled', draftSnapshot.anotherEventButtonEnabled)
      setFieldValue('anotherEventButtonLabel', draftSnapshot.anotherEventButtonLabel)
    }
    finishEditing()
  }

  const handleCustomLinkEdit = (link) => {
    startEditing(link.id, snapshotCustomLinkValues(link))
  }

  const handleCustomLinkCancel = (index, link) => {
    const isNewLink = String(link.id).startsWith('new')

    if (isNewLink) {
      const nextLinks = customLinks
        .filter((_, linkIndex) => linkIndex !== index)
        .map((tempLink, newIndex) => ({ ...tempLink, position: newIndex }))
      setFieldValue('customLinks', nextLinks)
      setFieldTouched('customLinks', true)
    } else if (draftSnapshot) {
      setFieldValue(`customLinks.${index}.content`, draftSnapshot.content)
      setFieldValue(`customLinks.${index}.url`, draftSnapshot.url)
      setFieldValue(`customLinks.${index}.enabled`, draftSnapshot.enabled)
    }

    finishEditing()
  }

  const deleteLink = (index) => {
    const removedLink = customLinks[index]
    const tempLinks = customLinks
      .filter((_, linkIndex) => linkIndex !== index)
      .map((tempLink, newIndex) => ({ ...tempLink, position: newIndex }))
    const isNewLink = String(removedLink.id).startsWith('new')

    if (isNewLink) {
      setFieldValue('customLinks', tempLinks)
    } else {
      setFieldValue('customLinks', [...tempLinks, {
        ...removedLink,
        enabled: false,
        _destroy: true,
      }])
    }

    setFieldTouched('customLinks', true)

    if (editingKey === removedLink.id) {
      finishEditing()
    }
  }

  const handleAddLink = () => {
    enforceUpgrade(() => {
      const newCustomLink = generateBlankCustomLink(undeletedLinks.length)
      setFieldValue('customLinks', [...customLinks, newCustomLink])
      startEditing(newCustomLink.id, snapshotCustomLinkValues(newCustomLink))
    })
  }

  if (confirmationPageType === EXTERNAL) {
    return null
  }

  return (
    <>
      <hr className={sectionDividerCss} />
      <div className={subsectionCss} data-testid="confirmation-internal-section">
        <div className={subsectionHeaderCss}>
          <TitleText level={3}>
            {I18n.t('publisher.event_type_editor_crud.confirmation.additional_links_title')}
          </TitleText>
          <BodyText color="muted" size="small">
            {I18n.t('publisher.event_type_editor_crud.confirmation.additional_links_description')}
          </BodyText>
        </div>
        <div className={linkListCss}>
          <ScheduleAnotherMeetingCard
            disabled={isEditing}
            editingKey={editingKey}
            onCancel={handleScheduleAnotherCancel}
            onDone={finishEditing}
            onEdit={handleScheduleAnotherEdit}
          />
          <VerticalSortableContext items={undeletedLinks} setItems={handleReorder}>
            {customLinks.map((link, index) => {
              if (link._destroy) return null

              return (
                <CustomLinkCard
                  key={link.id}
                  disabled={isEditing}
                  editingKey={editingKey}
                  index={index}
                  link={link}
                  onCancel={() => handleCustomLinkCancel(index, link)}
                  onDelete={() => deleteLink(index)}
                  onDone={finishEditing}
                  onEdit={() => handleCustomLinkEdit(link)}
                />
              )
            })}
          </VerticalSortableContext>
        </div>
        <Button
          appearance="ghost"
          className={addLinkButtonCss}
          data-testid="add-custom-link-button"
          disabled={isEditing}
          size="small"
          type="button"
          onClick={handleAddLink}
        >
          <span className={addLinkButtonContentCss}>
            <UiIcon icon={IconPlus} size="small" color={colorIconStandard} title="" />
            <span className={addLinkButtonTextCss}>
              {I18n.t('publisher.event_type_editor.sections.more_options.confirmation_page.add_new_link')}
            </span>
          </span>
        </Button>
      </div>
    </>
  )
}
